"use client";

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "next/navigation";
import { Icon } from "@/components/ui/Icon";
import { vtc } from "@/config/infos";
import { TransportVoucherData } from "@/types/navigation";
import Link from "next/link";
import { NUMBER_FIELDS, BOOLEAN_FIELDS, JSON_FIELDS } from "./voucherFields";
import { buildSharedToken, readSharedToken } from "./voucherToken";
import { VoucherActionsBar } from "./VoucherActionsBar";
import { VoucherEditForm } from "./VoucherEditForm";
import { VoucherPreview } from "./VoucherPreview";

// Bon de transport VIERGE : infos VTC de l'entreprise pré-remplies (config),
// le reste à compléter manuellement. Utilisé en accès libre (URL sans
// paramètre) et par le bouton « créer un nouveau bon ».
const buildBlankVoucher = (): TransportVoucherData => ({
  entreprise: vtc.entreprise,
  siren: vtc.siren,
  registreVTC: vtc.registreVTC,
  telPro: vtc.telPro,
  mailPro: vtc.mailPro,
  chauffeur: vtc.chauffeur,
  vehicule: vtc.vehicule,
  modele: vtc.modele,
  immatriculation: vtc.immatriculation,
  bonCommande: `${vtc.prefixe}-${Date.now().toString().slice(-6)}`,
  estimatedPrice: 0,
  passengers: 1,
  luggage: 0,
  date: new Date().toISOString().split("T")[0],
  time: new Date().toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
  }),
  modePaiement: vtc.modePaiement,
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  tripType: "one-way",
  departure: "",
  arrival: "",
  babySeats: 0,
  childSeats: 0,
});

// Composant client qui utilise useSearchParams
export const TransportVoucherClient = () => {
  const searchParams = useSearchParams();
  const hasShareToken = searchParams.has("d") || searchParams.has("m");
  const sharedData = useMemo(
    () => readSharedToken(searchParams),
    [searchParams],
  );
  const isReadOnly = !!sharedData;
  const parsedVoucherData = useMemo((): TransportVoucherData | null => {
    if (sharedData) {
      return sharedData;
    }
    const params: Record<string, unknown> = {};
    searchParams.forEach((value, key) => {
      if (key === "stops") {
        try {
          params[key] = JSON.parse(value);
        } catch {
          params[key] = [];
        }
      } else if (NUMBER_FIELDS.has(key)) {
        params[key] = parseFloat(value);
      } else if (BOOLEAN_FIELDS.has(key)) {
        params[key] = value === "true";
      } else if (JSON_FIELDS.has(key)) {
        try {
          params[key] = JSON.parse(value);
        } catch {
          params[key] = value;
        }
      } else {
        params[key] = value;
      }
    });

    if (Object.keys(params).length === 0) {
      return null;
    }

    return params as TransportVoucherData;
  }, [searchParams, sharedData]);

  const [data, setData] = useState<TransportVoucherData | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState<TransportVoucherData | null>(
    null,
  );
  const [isModified, setIsModified] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [shareStatus, setShareStatus] = useState<string | null>(null);
  const [isBootstrapped, setIsBootstrapped] = useState(false);
  const effectiveIsEditing = !isReadOnly && isEditing;

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      if (parsedVoucherData) {
        setData(parsedVoucherData);
        setEditedData(parsedVoucherData);
      } else if (!hasShareToken) {
        // Accès libre : aucune donnée dans l'URL → bon de transport VIERGE,
        // directement éditable et remplissable manuellement (pas de blocage).
        const blank = buildBlankVoucher();
        setData(blank);
        setEditedData(blank);
        setIsEditing(true);
      }
      setIsBootstrapped(true);
    });
    return () => cancelAnimationFrame(frame);
  }, [parsedVoucherData, hasShareToken]);

  const handleInputChange = (field: string, value: any) => {
    if (isReadOnly) return;
    setValidationError(null);
    setEditedData((prev) => {
      if (!prev) return null;
      return {
        ...prev,
        [field]: value,
      };
    });
    // Sauvegarder dans le localStorage à chaque modification
    localStorage.setItem(
      "transportVoucherData",
      JSON.stringify({
        ...editedData,
        [field]: value,
      }),
    );
  };

  const validateForm = () => {
    if (!editedData) return false;

    // Vérifier les champs obligatoires
    if (!editedData.departure) {
      setValidationError("Le lieu de départ est obligatoire");
      return false;
    }
    if (!editedData.date) {
      setValidationError("La date est obligatoire");
      return false;
    }
    if (!editedData.time) {
      setValidationError("L'heure est obligatoire");
      return false;
    }

    // Validation du SIRET/SIREN uniquement si une entreprise est sélectionnée
    if (editedData.isEntreprise) {
      if (!editedData.entrepriseName) {
        setValidationError("Le nom de l'entreprise est obligatoire");
        return false;
      }
      if (!editedData.siret) {
        setValidationError(
          "Le SIRET ou SIREN est obligatoire pour une entreprise",
        );
        return false;
      }
      // Vérifier que le SIRET/SIREN contient uniquement des chiffres
      if (!/^\d+$/.test(editedData.siret)) {
        setValidationError("Le SIRET/SIREN ne doit contenir que des chiffres");
        return false;
      }
      // Vérifier que la longueur est soit 9 (SIREN) soit 14 (SIRET)
      if (editedData.siret.length !== 9 && editedData.siret.length !== 14) {
        setValidationError(
          "Le numéro doit être un SIREN (9 chiffres) ou un SIRET (14 chiffres)",
        );
        return false;
      }
    }

    return true;
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    if (isReadOnly) return;

    if (!validateForm() || !editedData) {
      return;
    }

    setData({ ...editedData, isModified: true });
    setIsModified(true);
    setIsEditing(false);
    // Réinitialiser le localStorage après la sauvegarde
    localStorage.removeItem("transportVoucherData");
  };

  const handleNewPDF = () => {
    if (isReadOnly) return;
    setIsEditing(true);
    const defaultData = buildBlankVoucher();
    setData(defaultData);
    setEditedData(defaultData);
    localStorage.setItem("transportVoucherData", JSON.stringify(defaultData));
  };

  const handleCopyReadOnlyLink = async () => {
    const source = effectiveIsEditing && editedData ? editedData : data;
    if (!source || typeof window === "undefined") return;

    try {
      const query = buildSharedToken(source);
      const url = `${window.location.origin}/bon-de-transport?${query}`;
      await navigator.clipboard.writeText(url);
      setShareStatus("Lien VTC copié.");
      window.setTimeout(() => setShareStatus(null), 2200);
    } catch {
      setShareStatus("Impossible de copier le lien.");
      window.setTimeout(() => setShareStatus(null), 2200);
    }
  };

  // Évite le "flash" du CTA de création avant l'initialisation des données.
  if (!isBootstrapped) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white px-4">
        <div className="text-center">
          <h1 className="mb-4 text-2xl font-bold text-primary">
            Chargement du bon de transport...
          </h1>
          <p className="text-black/70">
            Veuillez patienter pendant que nous préparons votre document.
          </p>
        </div>
      </div>
    );
  }

  // Si un token de partage est présent mais invalide, refuser l'accès.
  if (hasShareToken && !sharedData) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center bg-white px-4 py-20">
        <div className="text-center">
          <h1 className="mb-4 text-2xl font-bold text-primary">
            Lien invalide
          </h1>
          <p className="mb-8 text-black/70">
            Ce lien de partage est invalide ou a été modifié.
          </p>
          <Link
            href="/"
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 font-medium text-black transition-colors hover:bg-primary/90"
          >
            <Icon icon="ph:arrow-left" className="h-5 w-5" />
            <span>Retour à l&apos;accueil</span>
          </Link>
        </div>
      </div>
    );
  }

  if (!data || Object.keys(data).length === 0) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white px-4 text-black">
        <div className="max-w-xl rounded-2xl border border-black/10 bg-white p-6 text-center shadow-[0_14px_40px_-30px_rgba(0,0,0,0.45)]">
          <h1 className="mb-4 text-2xl font-bold text-primary">
            Créer un nouveau bon de transport
          </h1>
          <p className="mb-8 text-black/70">
            Aucune donnée n&apos;est disponible. Voulez-vous créer un nouveau
            bon de transport ?
          </p>
          <button
            onClick={handleNewPDF}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 font-medium text-black transition-colors hover:bg-primary/90"
          >
            <Icon icon="ph:plus-circle" className="h-5 w-5" />
            <span>Créer un nouveau bon de transport</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-20 text-black">
      <div className="mx-auto w-full max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
        <div className="mb-2">
          <h1 className="text-xl font-semibold text-primary sm:text-2xl">
            Bon de transport
          </h1>
        </div>
        <div className="mb-6 rounded-2xl border border-black/10 bg-black/[0.02] p-4 sm:p-6">
          <h2 className="text-2xl font-light uppercase tracking-[0.14em] text-black sm:text-3xl">
            Bon de transport VTC
          </h2>
          <p className="mt-2 text-sm text-black/70 sm:text-base">
            Téléchargez le document, partagez un lien en lecture seule à un
            autre VTC, ou modifiez les informations si nécessaire.
          </p>
          {isReadOnly && (
            <p className="mt-3 rounded-lg border border-amber-400/40 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              Mode lecture seule actif : la modification du bon est désactivée.
            </p>
          )}
        </div>

        <VoucherActionsBar
          data={data}
          isReadOnly={isReadOnly}
          effectiveIsEditing={effectiveIsEditing}
          onToggleEditing={() => setIsEditing((prev) => !prev)}
          onCopyReadOnlyLink={handleCopyReadOnlyLink}
          shareStatus={shareStatus}
        />

        {/* Affichage des erreurs de validation */}
        {validationError && (
          <div className="mb-4 w-full rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
            <div className="flex items-center gap-2">
              <Icon icon="ph:warning-circle" className="h-5 w-5" />
              <span>{validationError}</span>
            </div>
          </div>
        )}

        <div className="flex w-full flex-col gap-6 lg:flex-row">
          {/* Formulaire d'édition */}
          {effectiveIsEditing && editedData && (
            <VoucherEditForm
              editedData={editedData}
              onInputChange={handleInputChange}
              onSubmit={handleSave}
              onCancel={() => setIsEditing(false)}
            />
          )}

          {/* Aperçu PDF */}
          <VoucherPreview
            data={data}
            editedData={editedData}
            effectiveIsEditing={effectiveIsEditing}
          />
        </div>
      </div>
    </div>
  );
};
